// buildbotsnow.com/help/page-context: what an owner, and the developer who
// installs the chat for them, needs to know to let the assistant see the page
// a visitor is on (Luis, November 2, 2026). The rules here are the ones
// functions/lib/pageContext.js and widget/build-bots-now.js enforce; the
// numbers come from the same constants.
//
// Public and prerendered, like the comparison page (src/prerender.tsx).

import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PRODUCT_NAME } from "../lib/brand";
import { SET_VISITOR_EXAMPLE } from "../lib/pageContext";
import { ColorModeToggle } from "../components/ColorModeToggle";
import Footer from "../components/Footer";
import { Logo } from "../components/Logo";

export const PAGE_CONTEXT_GUIDE_TITLE = `Page context: let your assistant see the page | ${PRODUCT_NAME}`;

// Kept equal to the server's limits by PageContextGuide.test.tsx.
export const GUIDE_LIMITS = { pageChars: 12000, dataKb: 32, depth: 8, descriptionChars: 4000 };

function Code({ children }: { children: string }) {
  return <pre className="mt-3 overflow-x-auto rounded-xl bg-slate-900 p-4 text-sm leading-relaxed text-slate-100">{children}</pre>;
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="mt-12 scroll-mt-20">
      <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
      <div className="mt-4 space-y-3 leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}

const SHOP_EXAMPLE = `<script src="https://buildbotsnow.com/build-bots-now.js" data-agent="YOUR_AGENT_ID" async></script>
<script>
  // The chat script loads on its own; hand it the data once it is there.
  function tellTheChat() {
    BuildBotsNow.setContext({
      plan: "Pro",
      lastOrder: { id: "A-1042", status: "shipped", arrives: "November 6" }
    });
  }
  if (window.BuildBotsNow) tellTheChat();
  else window.addEventListener("load", tellTheChat);
</script>`;

const SIGN_IN_EXAMPLE = `// Wherever your site learns who is signed in (its sign-in listener),
// on every page that has the chat:
onSignInChanged((user) => {
  window.BuildBotsNow?.setVisitor(user ? user.id : null);
});

// And when you pass their data, say whose it is:
BuildBotsNow.setContext(data, { visitor: user.id });`;

const REFERENCE: [string, string][] = [
  ["BuildBotsNow.setContext(data)", "Hand every assistant on the page data about this visitor. null clears it."],
  ['BuildBotsNow.setContext(data, { agent: "ID" })', "The same, for one assistant when a page has several."],
  ['BuildBotsNow.setContext(data, { visitor: "USER_ID" })', "The same, and say whose data it is."],
  ['BuildBotsNow.init({ agent: "ID", context: data })', "Start the chat with data already set."],
  ["BuildBotsNow.setVisitor(id or null)", "Say who is signed in, or that nobody is."],
  ["BuildBotsNow.reset()", "Forget the conversation and its data in this browser, now."]
];

export default function PageContextGuide() {
  useEffect(() => {
    const title = document.title;
    document.title = PAGE_CONTEXT_GUIDE_TITLE;
    return () => {
      document.title = title;
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" aria-label={`${PRODUCT_NAME} home`} className="flex">
            <Logo />
          </Link>
          <ColorModeToggle />
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10">
        <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">Page context: let your assistant see the page</h1>
        <p className="mt-3 text-slate-600">
          Out of the box, your assistant answers from your knowledge: your website, documents and Q&amp;A. Page context lets it also
          answer about what this visitor is looking at right now: the product on screen, their order, their booking, their results.
          It is part of the paid plan, and it is off until you turn it on.
        </p>

        <nav aria-label="On this page" className="mt-6 rounded-2xl border border-slate-200 bg-surface p-5 text-sm">
          <p className="font-semibold text-slate-900">Two ways to use it</p>
          <ul className="mt-2 space-y-1 text-slate-700">
            <li>
              <a href="#read-the-page" className="text-brand-700 underline">Read the page</a>: no code. Most businesses only need this.
            </li>
            <li>
              <a href="#data-from-code" className="text-brand-700 underline">Data from your website&apos;s code</a>: one line from your
              developer, for exact facts or ones not on the screen.
            </li>
            <li>
              Plus: <a href="#sign-in" className="text-brand-700 underline">sites where customers sign in</a>,{" "}
              <a href="#rules" className="text-brand-700 underline">the rules</a>,{" "}
              <a href="#check" className="text-brand-700 underline">checking it works</a> and{" "}
              <a href="#reference" className="text-brand-700 underline">the full reference</a>.
            </li>
          </ul>
        </nav>

        <Section id="read-the-page" title="1. Read the page (no code)">
          <p>For the owner, in your dashboard:</p>
          <ol className="list-decimal space-y-1 pl-6">
            <li>Open your assistant, then the <strong>Setup</strong> tab.</li>
            <li>
              Open <strong>Page context</strong> and tick <strong>Read the page the visitor is on</strong>.
            </li>
            <li>
              Optional: under <strong>What the page and its data mean</strong>, add anything that helps it read your pages, like
              &quot;Prices on product pages include tax.&quot;
            </li>
            <li>
              Press <strong>Save changes</strong>.
            </li>
          </ol>
          <p>
            From then on, each time a visitor sends a question, the chat sends the assistant the page&apos;s address (without anything
            after a &quot;?&quot; or &quot;#&quot;), its title, and the text the visitor can see, up to{" "}
            {GUIDE_LIMITS.pageChars.toLocaleString("en-US")} characters. It sends the page again only when it has changed.
          </p>
          <p>
            <strong>Left out on purpose:</strong> menus, headers, footers, sidebars, pop-ups and cookie banners, anything hidden, and
            every form, so nothing a visitor types into your page is ever read.
          </p>
          <p>
            <strong>Tip for your developer:</strong> if your pages wrap their main content in a <code>&lt;main&gt;</code> element, the
            assistant reads only that, which is the cleanest result.
          </p>
        </Section>

        <Section id="data-from-code" title="2. Data from your website's code">
          <p>
            Use this when the assistant needs facts that are not on the screen (an order&apos;s delivery date, a customer&apos;s plan, a
            history) or that must be exact.
          </p>
          <p>
            <strong>The owner:</strong> under Setup, Page context, tick <strong>Accept data from your website&apos;s code</strong>. Then
            describe the data in plain words under <strong>What the page and its data mean</strong>: what each field is and what its
            values mean. The assistant knows only what you write there, so be specific: &quot;status is one of placed, shipped or
            delivered&quot;.
          </p>
          <p>
            <strong>The developer:</strong> after the chat script, hand it the data as plain JSON: objects, lists, text, numbers and
            true or false.
          </p>
          <Code>{SHOP_EXAMPLE}</Code>
          <ul className="list-disc space-y-1 pl-6">
            <li>Call it again whenever the data changes; the chat sends it with the next question, and only when it changed.</li>
            <li>
              <code>BuildBotsNow.setContext(null)</code> clears it.
            </li>
            <li>
              Up to {GUIDE_LIMITS.dataKb} KB, nested at most {GUIDE_LIMITS.depth} levels. Send a summary of what a visitor would ask
              about, not a whole database.
            </li>
            <li>Both ways can be on together: the assistant then sees the page and your data.</li>
          </ul>
        </Section>

        <Section id="sign-in" title="3. Sites where customers sign in">
          <p>
            The chat remembers a conversation in the visitor&apos;s browser for up to 12 hours, so it follows them from page to page.
            On a site with accounts, a shared computer must not show one customer&apos;s conversation to the next. The chat handles
            that for you:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>With no code:</strong> a conversation that was given data from your code stays on the page where it happened.
              Signing out, or moving to another page, starts a fresh chat.
            </li>
            <li>
              <strong>With one line:</strong> tell the chat who is signed in, and their conversation follows them to every page,
              then disappears the moment they sign out or someone else signs in.
            </li>
          </ul>
          <Code>{SIGN_IN_EXAMPLE}</Code>
          <p>
            Use your own account id (never an email address or a password). The chat keeps only a short scrambled version of it, to
            tell two people apart. <code>{SET_VISITOR_EXAMPLE}</code> is all it needs.
          </p>
        </Section>

        <Section id="rules" title="4. The rules">
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Only what this visitor can already see.</strong> Never pass another customer&apos;s data, staff notes, or
              anything secret.
            </li>
            <li>
              <strong>It is not proof of who someone is.</strong> Anything a page hands over, the visitor can edit in their own
              browser. The assistant uses it to answer, never to unlock anything, apply a discount or change an order. For looking up
              accounts securely, connect your own API instead, on the Integrations tab.
            </li>
            <li>
              <strong>Data, not instructions.</strong> The assistant is told never to follow instructions written inside page
              context, so a visitor cannot use it to change how the assistant behaves.
            </li>
            <li>
              <strong>Your privacy policy.</strong> What the assistant sees is kept with the conversation for 30 days, where your
              team can read it. If your pages show personal details, say in your privacy policy that your chat assistant may read
              them.
            </li>
          </ul>
        </Section>

        <Section id="check" title="5. Checking it works">
          <ol className="list-decimal space-y-1 pl-6">
            <li>Open your website, open the chat, and ask about what is on the page: &quot;What does this one cost?&quot;</li>
            <li>
              In your dashboard, open the assistant&apos;s <strong>Conversations</strong> tab and that conversation. Above the
              messages, <strong>The page they were on</strong> and <strong>What the page told the assistant</strong> show exactly
              what it saw.
            </li>
            <li>
              Nothing there? Your developer can open the browser console: if the assistant ignored what the page sent, it says why,
              in a line starting <code>[build-bots-now]</code>. The usual reasons are the setting being off or the data being too
              big.
            </li>
          </ol>
        </Section>

        <Section id="reference" title="6. Reference for developers">
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-surface">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">Call</th>
                  <th scope="col" className="px-4 py-3 font-medium">What it does</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {REFERENCE.map(([call, what]) => (
                  <tr key={call}>
                    <td className="px-4 py-3 align-top">
                      <code className="whitespace-nowrap text-slate-900">{call}</code>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-700">{what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            The chat script loads on its own, so <code>window.BuildBotsNow</code> may not exist yet when your code runs. Wait for the
            page&apos;s <code>load</code> event, as in the examples. The description box holds up to{" "}
            {GUIDE_LIMITS.descriptionChars.toLocaleString("en-US")} characters.
          </p>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
